/**
 * Invite Clients Modal
 *
 * Modal component for sending mass BCC invitations to attorney clients.
 * Allows doctors to select multiple attorneys and customize the email
 * before sending via server-side Mailgun integration.
 *
 * @module components/invite-clients-modal
 */

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Send,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiClient } from '../api';
import { useEncryption } from '../contexts/EncryptionContext';
import {
  type AttorneyContact,
  generateInviteEmailBody,
  generateInviteEmailSubject,
  trackInviteEvent,
} from '../utils/inviteEmailUtils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';

interface InviteClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  attorneys: AttorneyContact[];
  doctorName: string;
  specialty?: string;
  vanityUrl?: string;
  /** If true, fetches attorneys from server (decrypted). If false, uses passed attorneys prop. */
  fetchFromServer?: boolean;
}

type ModalStep = 'select' | 'compose';

export function InviteClientsModal({
  isOpen,
  onClose,
  attorneys: propAttorneys,
  doctorName,
  specialty,
  vanityUrl,
  fetchFromServer = false,
}: InviteClientsModalProps) {
  // Get encryption context for decrypting attorney data
  const { decryptField, isUnlocked } = useEncryption();

  // Step management
  const [step, setStep] = useState<ModalStep>('select');

  // Attorney selection
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [attorneys, setAttorneys] = useState<AttorneyContact[]>(propAttorneys);
  const [isLoadingAttorneys, setIsLoadingAttorneys] = useState(false);

  // Email composition
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Sending state
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add new contact form
  const [showAddContact, setShowAddContact] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [manualContacts, setManualContacts] = useState<AttorneyContact[]>([]);

  // Decrypt a field if it's encrypted (async because decryptField returns a Promise)
  const decryptIfNeeded = useCallback(
    async (value: string | null | undefined, caseId?: string): Promise<string> => {
      if (!value) return '';
      if (
        (value.startsWith('ENC:') || value.startsWith('HENC:') || value.startsWith('CASEKEY:')) &&
        isUnlocked &&
        decryptField
      ) {
        try {
          const decrypted = await decryptField(value, caseId);
          return decrypted || value;
        } catch (e) {
          console.warn('Failed to decrypt field:', e);
          return value;
        }
      }
      return value;
    },
    [decryptField, isUnlocked],
  );

  // Initialize email content when doctor info changes or modal opens
  useEffect(() => {
    if (isOpen && doctorName) {
      setSubject(generateInviteEmailSubject(doctorName));
      setBody(generateInviteEmailBody(doctorName, specialty, vanityUrl));
    }
  }, [isOpen, doctorName, specialty, vanityUrl]);

  // Fetch attorneys from cases and decrypt client-side
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dep list; loader fn identity changes every render (pre-existing pattern)
  useEffect(() => {
    if (isOpen && fetchFromServer) {
      fetchAndDecryptAttorneys();
    } else if (isOpen && !fetchFromServer) {
      setAttorneys(propAttorneys);
    }
  }, [isOpen, fetchFromServer, propAttorneys, isUnlocked]);

  const fetchAndDecryptAttorneys = async () => {
    setIsLoadingAttorneys(true);
    setError(null);
    try {
      // Fetch cases from the existing cases API (returns array directly)
      const cases = await apiClient.getCases();

      // Extract and decrypt unique attorneys
      const attorneyMap = new Map<string, AttorneyContact>();

      for (const caseItem of cases) {
        // Get email (should be plaintext per hybrid encryption strategy)
        const email = (caseItem.attorneyEmail || '').toLowerCase().trim();
        if (!email || attorneyMap.has(email)) continue;
        // Skip if email looks encrypted (shouldn't happen)
        if (email.startsWith('enc:')) continue;

        // Decrypt name and firm if needed (ensure string type)
        let name = String(
          (await decryptIfNeeded(caseItem.attorneyName, caseItem.id)) || 'Unknown Attorney',
        );
        let firmName = String(
          (await decryptIfNeeded(caseItem.firmName, caseItem.id)) || 'Unknown Firm',
        );

        // Use fallbacks for encrypted values that couldn't be decrypted
        if (name.startsWith('ENC:') || name.startsWith('HENC:') || name.startsWith('CASEKEY:')) {
          name = 'Attorney';
        }
        if (
          firmName.startsWith('ENC:') ||
          firmName.startsWith('HENC:') ||
          firmName.startsWith('CASEKEY:')
        ) {
          firmName = 'Law Firm';
        }

        attorneyMap.set(email, { email, name, firmName });
      }

      // Sort by firm, then name
      const attorneys = Array.from(attorneyMap.values()).sort((a, b) => {
        const firmCompare = a.firmName.localeCompare(b.firmName);
        if (firmCompare !== 0) return firmCompare;
        return a.name.localeCompare(b.name);
      });

      setAttorneys(attorneys);
    } catch (err) {
      console.error('Failed to fetch attorneys:', err);
      setError('Failed to load attorney list');
      toast.error('Failed to load attorney list');
    } finally {
      setIsLoadingAttorneys(false);
    }
  };

  const handleToggleAttorney = (email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  // Combine attorneys from cases with manually added contacts
  const allContacts = [...attorneys, ...manualContacts];

  const handleSelectAll = () => {
    if (selectedEmails.size === allContacts.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(allContacts.map((a) => a.email)));
    }
  };

  const handleAddContact = () => {
    // Validate
    if (!newFirstName.trim() || !newLastName.trim() || !newEmail.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    const email = newEmail.trim().toLowerCase();

    // Check for duplicates
    if (allContacts.some((c) => c.email === email)) {
      toast.error('This email is already in the list');
      return;
    }

    const newContact: AttorneyContact = {
      email,
      name: `${newFirstName.trim()} ${newLastName.trim()}`,
      firmName: 'Manual Entry',
    };

    setManualContacts((prev) => [...prev, newContact]);
    setSelectedEmails((prev) => new Set([...prev, email])); // Auto-select new contact

    // Reset form
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setShowAddContact(false);

    toast.success('Contact added');
  };

  const handleRemoveManualContact = (email: string) => {
    setManualContacts((prev) => prev.filter((c) => c.email !== email));
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });
  };

  const handleNext = () => {
    setStep('compose');
  };

  const handleBack = () => {
    setStep('select');
  };

  const handleSendEmails = async () => {
    if (selectedEmails.size === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    if (!body.trim()) {
      toast.error('Please enter an email message');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const recipients = Array.from(selectedEmails);

      trackInviteEvent('invite_email_composed', {
        recipientCount: recipients.length,
        source: 'invite-modal',
      });

      const result = await apiClient.sendInvitationEmails({
        recipients,
        subject: subject.trim(),
        body: body.trim(),
      });

      toast.success(`Invitation sent to ${result.sent} attorneys`);
      handleClose();
    } catch (err: any) {
      console.error('Failed to send invitation emails:', err);
      const errorMessage = err?.message || 'Failed to send invitation emails';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setSelectedEmails(new Set());
    setStep('select');
    setError(null);
    setIsSending(false);
    // Reset add contact form
    setShowAddContact(false);
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setManualContacts([]);
    onClose();
  };

  const allSelected = selectedEmails.size === allContacts.length && allContacts.length > 0;
  const someSelected = selectedEmails.size > 0 && selectedEmails.size < allContacts.length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Mail className="h-5 w-5" />
            Invite Clients to ExpertMD
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {step === 'select'
              ? "Select attorneys to invite via email. Recipients will be BCC'd so they can't see each other's addresses."
              : 'Review and customize your invitation email before sending.'}
          </DialogDescription>
        </DialogHeader>

        {/* Loading state */}
        {isLoadingAttorneys && (
          <div className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#A66B32] mx-auto mb-3" />
            <p className="text-gray-600">Loading attorneys...</p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoadingAttorneys && attorneys.length === 0 && (
          <div className="py-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Error loading attorneys</p>
            <p className="text-gray-500 text-sm mt-1">{error}</p>
            <Button variant="outline" onClick={fetchAndDecryptAttorneys} className="mt-4">
              Try Again
            </Button>
          </div>
        )}

        {/* Step 1: Select attorneys */}
        {!isLoadingAttorneys && step === 'select' && (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Select All Header */}
            {allContacts.length > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all"
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    className="data-[state=indeterminate]:bg-[#A66B32] data-[state=indeterminate]:border-[#A66B32]"
                    data-state={
                      someSelected ? 'indeterminate' : allSelected ? 'checked' : 'unchecked'
                    }
                  />
                  <Label
                    htmlFor="select-all"
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Select all ({allContacts.length} contacts)
                  </Label>
                </div>
                <Badge className="bg-[#EAE0CB]/50 text-[#3A2718] border-[#3A2718]/15">
                  {selectedEmails.size} selected
                </Badge>
              </div>
            )}

            {/* Contact List - Flat inline display */}
            {allContacts.length > 0 && (
              <ScrollArea className="flex-1 overflow-auto" style={{ maxHeight: '200px' }}>
                <div className="space-y-1 py-2 pr-4">
                  {allContacts.map((contact) => {
                    const isManual = manualContacts.some((c) => c.email === contact.email);
                    return (
                      <div
                        key={contact.email}
                        className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <Checkbox
                          id={`contact-${contact.email}`}
                          checked={selectedEmails.has(contact.email)}
                          onCheckedChange={() => handleToggleAttorney(contact.email)}
                        />
                        <Label
                          htmlFor={`contact-${contact.email}`}
                          className="flex-1 cursor-pointer flex items-center gap-2 min-w-0"
                        >
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {contact.name}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500 truncate">{contact.firmName}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-400 truncate">{contact.email}</span>
                        </Label>
                        {isManual && (
                          <button
                            type="button"
                            onClick={() => handleRemoveManualContact(contact.email)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                            title="Remove contact"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            {/* Empty state for no contacts */}
            {allContacts.length === 0 && !showAddContact && (
              <div className="py-6 text-center">
                <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">No contacts yet</p>
                <p className="text-gray-500 text-xs mt-1">Add contacts manually below</p>
              </div>
            )}

            {/* Add Contact Section */}
            <div className="border-t border-gray-200 pt-3 mt-2">
              {!showAddContact ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddContact(true)}
                  className="w-full text-gray-700 border-dashed"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add New Contact
                </Button>
              ) : (
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Add New Contact</span>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddContact(false);
                        setNewFirstName('');
                        setNewLastName('');
                        setNewEmail('');
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="First name"
                      value={newFirstName}
                      onChange={(e) => setNewFirstName(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Last name"
                      value={newLastName}
                      onChange={(e) => setNewLastName(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="text-sm"
                  />
                  <Button
                    type="button"
                    onClick={handleAddContact}
                    className="w-full bg-[#3A2718] hover:bg-[#241610] text-white text-sm"
                    size="sm"
                  >
                    Add Contact
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Compose email */}
        {!isLoadingAttorneys && step === 'compose' && (
          <div className="flex flex-col flex-1 min-h-0 space-y-4">
            {/* Recipients list */}
            <div className="py-2 px-3 bg-[#EAE0CB]/40 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-[#A66B32]" />
                <span className="text-sm font-medium text-[#3A2718]">
                  Sending to {selectedEmails.size} recipient{selectedEmails.size !== 1 ? 's' : ''}{' '}
                  (BCC):
                </span>
              </div>
              <div className="text-sm text-[#A66B32] pl-6">
                {allContacts
                  .filter((a) => selectedEmails.has(a.email))
                  .map((a) => a.name)
                  .join(', ')}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="email-subject" className="text-sm font-medium text-gray-700">
                Subject
              </Label>
              <Input
                id="email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject..."
                className="w-full"
              />
            </div>

            {/* Body */}
            <div className="space-y-2 flex-1 flex flex-col">
              <Label htmlFor="email-body" className="text-sm font-medium text-gray-700">
                Message
              </Label>
              <Textarea
                id="email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Email message..."
                className="flex-1 min-h-[200px] resize-none"
              />
            </div>

            {/* Error display */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-shrink-0">
          {step === 'select' ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                className="text-gray-700 border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                disabled={selectedEmails.size === 0}
                className="bg-[#3A2718] hover:bg-[#241610] text-white"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isSending}
                className="text-gray-700 border-gray-300"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={handleSendEmails}
                disabled={isSending || !subject.trim() || !body.trim()}
                className="bg-[#3A2718] hover:bg-[#241610] text-white"
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
